import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Bell,
  Bike,
  Bug,
  CarFront,
  Gauge,
  HeartPulse,
  History,
  LogOut,
  Map,
  Music2,
  PanelLeftClose,
  PanelLeftOpen,
  QrCode,
  RadioTower,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  UserRound,
  Users,
  Waypoints,
  X,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { closeAdminSocket } from '@/services/socket';
import { api } from '@/services/api';

export const navigationItems = [
  { label: 'Overview', path: '/', icon: Gauge, group: 'Command' },
  { label: 'Live Monitoring', path: '/live-monitoring', icon: Map, group: 'Command' },
  { label: 'Convoys', path: '/convoys', icon: Waypoints, group: 'Operations' },
  { label: 'Clubs', path: '/clubs', icon: Users, group: 'Operations' },
  { label: 'Invite Hub', path: '/invite-hub', icon: QrCode, group: 'Operations' },
  { label: 'Riders', path: '/members', icon: UserRound, group: 'Directory' },
  { label: 'Vehicles', path: '/vehicles', icon: Bike, group: 'Directory' },
  { label: 'Rider Link', path: '/rider-link', icon: RadioTower, group: 'Control' },
  { label: 'Safety', path: '/safety', icon: ShieldAlert, group: 'Control' },
  { label: 'Notifications', path: '/notifications', icon: Bell, group: 'Control' },
  { label: 'Reports', path: '/reports', icon: BarChart3, group: 'Control' },
  { label: 'Error Center', path: '/backend-errors', icon: Bug, group: 'System' },
  { label: 'Settings', path: '/settings', icon: Settings, group: 'System' },
];

export const utilityNavigationItems = [
  { label: 'Voice Rooms', path: '/voice-rooms', icon: RadioTower, group: 'Rider Link', keywords: 'voice intercom livekit' },
  { label: 'Shared Music', path: '/shared-music', icon: Music2, group: 'Rider Link', keywords: 'music media audio' },
  { label: 'System Health', path: '/system-health', icon: HeartPulse, group: 'System', keywords: 'health runtime services' },
  { label: 'Audit Logs', path: '/audit-logs', icon: ScrollText, group: 'System', keywords: 'audit history' },
  { label: 'Security Events', path: '/security-events', icon: ShieldCheck, group: 'System', keywords: 'security event' },
  { label: 'Map Identity', path: '/settings/map-identity', icon: History, group: 'System', keywords: 'map marker identity' },
];

export function Sidebar({ open = false, collapsed = false, onClose, onToggleCollapsed }) {
  const clear = useAuthStore((state) => state.clear);
  const notifications = useQuery({
    queryKey: ['sidebar-notification-summary'],
    queryFn: () => api('/api/v1/admin/notifications?limit=1').then((result) => result.summary ?? {}),
    refetchInterval: 15_000,
  });
  const badges = {
    '/notifications': Number(notifications.data?.unread ?? 0),
    '/safety': Number(notifications.data?.emergency ?? 0),
  };
  const groups = [...new Set(navigationItems.map((item) => item.group))];
  const width = collapsed ? 'lg:w-[72px]' : 'lg:w-[232px]';

  return <aside className={`command-sidebar fixed inset-y-0 left-0 z-40 flex w-[232px] flex-col border-r border-cyan-300/10 bg-[#040910]/98 backdrop-blur-2xl transition-[transform,width] duration-200 ${width} lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
    <div className={`relative flex h-[52px] shrink-0 items-center border-b border-cyan-300/10 ${collapsed ? 'justify-center px-2' : 'justify-between px-3'}`}>
      <div className="flex min-w-0 items-center gap-2">
        <div className="relative grid size-8 shrink-0 place-items-center border border-cyan-300/40 bg-cyan-300/[.06] text-cyan-300 shadow-[0_0_24px_rgba(34,211,238,.08)]"><CarFront size={16}/><span className="absolute -right-px -top-px size-1.5 bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,.9)]"/></div>
        {!collapsed && <div className="min-w-0"><p className="truncate text-[11px] font-black tracking-[.2em] text-slate-100">CONVOY</p><p className="truncate text-[6px] font-black uppercase tracking-[.24em] text-cyan-300/55">Operations Center</p></div>}
      </div>
      <button className="grid size-8 place-items-center border border-transparent text-slate-600 hover:border-white/10 hover:text-slate-200 lg:hidden" onClick={onClose}><X size={16}/></button>
    </div>

    {!collapsed && <div className="mx-2.5 mt-2.5 grid grid-cols-[1fr_auto] items-center border border-emerald-300/15 bg-emerald-300/[.04] px-2.5 py-1.5"><div><p className="text-[7px] font-black uppercase tracking-[.2em] text-slate-600">Operations</p><p className="mt-0.5 text-[8px] font-black uppercase tracking-[.14em] text-emerald-300">Network live</p></div><span className="cyber-pulse"/></div>}

    <nav className="scrollbar min-h-0 flex-1 overflow-y-auto px-2 py-2">
      {groups.map((group) => <div key={group} className="mb-2.5">
        {!collapsed && <p className="mb-1 px-2 text-[7px] font-black uppercase tracking-[.26em] text-slate-700">{group}</p>}
        <div className="space-y-0.5">{navigationItems.filter((item) => item.group === group).map(({ label, path, icon: Icon }) => <NavLink key={path} to={path} end={path === '/'} title={collapsed ? label : undefined} onClick={onClose} className={({ isActive }) => `group relative flex h-8 items-center border text-[10px] transition ${collapsed ? 'justify-center px-0' : 'gap-2 px-2.5'} ${isActive ? 'border-cyan-300/25 bg-cyan-300/[.075] text-cyan-100 shadow-[inset_2px_0_0_#67e8f9]' : 'border-transparent text-slate-500 hover:border-white/7 hover:bg-white/[.025] hover:text-slate-200'}`}>
          <Icon size={14}/>{!collapsed && <span className="font-semibold">{label}</span>}{badges[path] > 0 && <span className={`${collapsed ? 'absolute right-0.5 top-0.5 min-w-3 px-0.5 text-[6px]' : 'ml-auto min-w-5 px-1.5 text-[8px]'} bg-cyan-300 py-0.5 text-center font-mono font-black text-[#031015]`}>{Math.min(99, badges[path])}</span>}
        </NavLink>)}</div>
      </div>)}
    </nav>

    <div className="shrink-0 border-t border-cyan-300/10 p-2">
      <button title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} className="hidden h-8 w-full items-center justify-center gap-2 border border-transparent text-[10px] text-slate-600 transition hover:border-white/10 hover:text-cyan-200 lg:flex" onClick={onToggleCollapsed}>{collapsed ? <PanelLeftOpen size={14}/> : <><PanelLeftClose size={14}/><span>Collapse</span></>}</button>
      <button title={collapsed ? 'Sign out' : undefined} className={`flex h-8 w-full items-center border border-transparent text-[10px] text-slate-600 transition hover:border-rose-400/20 hover:bg-rose-400/[.05] hover:text-rose-300 ${collapsed ? 'justify-center' : 'gap-2 px-2.5'}`} onClick={() => { closeAdminSocket(); clear(); }}><LogOut size={14}/>{!collapsed && <span className="font-semibold">Sign out</span>}</button>
    </div>
  </aside>;
}
